import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView } from 'react-native';
import { Checkbox, Label, Paragraph, Separator, View, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { AppleSignInButton } from '@/components/apple-signin-button';
import { GoogleSignInButton } from '@/components/google-signin-button';
import { Heading } from '@/components/heading';
import { Input } from '@/components/input';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { ScreenHeader } from '@/components/screen-header';
import { TextLink } from '@/components/text-link';
import { useAuth } from '@/hooks/auth-context';
import { api } from '@/lib/api';
import { type AuthMode, initialAuthMode } from '@/lib/auth-mode';
import { MIN_PASSWORD_LENGTH } from '@/lib/password';
import { queryKeys } from '@/lib/query-keys';
import { auth as authClient } from '@/lib/auth';
import { borderWidth, space } from '@/theme/tokens.generated';

// Where Supabase should send the browser back to once an OAuth redirect
// or password-reset email link completes. Always the app root, not
// /sign-up: an already signed-in user re-running OAuth (e.g. from a
// stale tab) would otherwise land on a URL whose route group is gated
// off for them, with no screen to show. `/` re-enters
// through the normal auth gate regardless of status. Only meaningful
// on web -- native's redirect-based flow needs a deep link +
// WebBrowser instead, not built yet (issue #11's native gap).
function redirectTo() {
  return Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.origin : undefined;
}

export function SignUp() {
  const auth = useAuth();
  const router = useRouter();

  // This modal's own dismiss action -- previously the native header's
  // headerLeft ((unauth)/_layout.tsx), moved in-body along with
  // headerShown: false so removing the duplicate "Sign up" title didn't
  // also remove the only way to leave this screen on web (native still
  // gets swipe-to-dismiss from the modal presentation either way).
  const close = () => router.back();

  // Once Supabase Auth has succeeded (OAuth or email/password), the
  // only thing left is age-attestation -- the auth-method picker below
  // has nothing left to offer someone who's already authenticated.
  return auth.status === 'needs-signup' ? <AgeGateStep onClose={close} /> : <AuthMethodStep onClose={close} />;
}

function AuthMethodStep({ onClose }: { onClose: () => void }) {
  // Toggles the email/password section between creating a new account
  // and signing back into an existing one. Without this there was no
  // way back in for a returning email/password user at all -- OAuth
  // naturally handles both cases through the same button (Supabase
  // links to the existing account), but signUp() and
  // signInWithPassword() are genuinely different calls.
  const [mode, setMode] = useState<AuthMode>(initialAuthMode(useLocalSearchParams<{ mode?: string }>().mode));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const google = useMutation({
    mutationFn: () => authClient.signInWithOAuth('google', redirectTo()),
  });

  const emailAuth = useMutation({
    mutationFn: async () => {
      if (mode === 'sign-in') {
        await authClient.signInWithPassword(email, password);
        return;
      }

      const { session } = await authClient.signUp(email, password);

      if (!session) {
        // Email confirmation is required by this Supabase project's
        // settings -- there's no session yet, so onAuthStateChange
        // won't fire and useAuth can't move past signed-out until the
        // user clicks the email link. Not a failure, but there's
        // nothing left for this screen to do, so it's surfaced through
        // the same error slot with a message that says so accurately.
        throw new Error('Check your email to confirm your account, then come back here to sign in.');
      }
    },
  });

  const forgotPassword = useMutation({
    mutationFn: async () => {
      // Was previously a silent no-op when email was empty (the button
      // press guard just skipped mutate()) -- the entry point to the
      // whole recovery flow, with no feedback at all on the single
      // most likely first attempt (a user who hasn't typed their email
      // yet). Throwing here routes it through the same error slot as
      // every other failure on this screen.
      if (!email) throw new Error('Enter your email above, then tap "Forgot password?" again.');
      await authClient.requestPasswordReset(email, redirectTo());
    },
  });

  const error = google.error ?? emailAuth.error ?? forgotPassword.error;

  return (
    <ScreenContainer>
      {/* ScrollView, not a fixed flex={1} YStack -- same structural bug
          class as create-thread/index.tsx's own fix (this session,
          founder-reported live): Google button, Apple button, a
          separator, two input fields each with their own helper/error
          text, a submit button, and a mode-toggle link, all in one
          non-scrolling column -- overflows on a short viewport
          (especially with the keyboard open and an inline error
          showing) without scrolling, it silently overlaps instead.
          Found by auditing every other screen for the same pattern
          after that report, not from a separate live report on this
          screen specifically. */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingTop: space[8], paddingBottom: space[6], gap: space[5] }}
        keyboardShouldPersistTaps="handled"
      >
        {/* Was "Verified real people only -- it only takes a minute,"
            a claim about the (until 2026-09-05) mandatory Tier 0
            liveness check every signup went through. Boardroom
            decision (~/.claude/boardroom/2026-09-05-tier0-
            verification-gating.md): Tier 0 is now opt-in, not a
            signup gate -- the population claim is no longer true, and
            "it only takes a minute" referred to a step that no longer
            happens during signup at all. */}
        <ScreenHeader
          label="Close"
          title={mode === 'sign-up' ? 'Sign up' : 'Sign in'}
          subtitle={mode === 'sign-up' ? 'Join real conversations happening near you.' : 'Welcome back.'}
          onBack={onClose}
        />

        <YStack gap="$3">
          <GoogleSignInButton
            disabled={Platform.OS !== 'web'}
            onPress={() => {
              if (!google.isPending) google.mutate();
            }}
          >
            {google.isPending
              ? 'Opening Google...'
              : Platform.OS !== 'web'
                ? 'Continue with Google (web only for now)'
                : 'Continue with Google'}
          </GoogleSignInButton>
          <YStack gap="$1">
            <AppleSignInButton disabled>Continue with Apple</AppleSignInButton>
            <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta" textAlign="center">
              Coming soon
            </Text>
          </YStack>
        </YStack>

        <XStack alignItems="center" gap="$3">
          <Separator />
          <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
            or
          </Text>
          <Separator />
        </XStack>

        <YStack gap="$4">
          <YStack gap="$2">
            <YStack gap="$1">
              <Label htmlFor="signup-email" color="$colorSecondary">
                Email
              </Label>
              <Input
                id="signup-email"
                value={email}
                onChangeText={(value) => {
                  setEmail(value);
                  if (forgotPassword.isSuccess || forgotPassword.isError) forgotPassword.reset();
                }}
                autoCapitalize="none"
                keyboardType="email-address"
                textContentType="emailAddress"
              />
              {/* Password below already has an always-visible helper;
                  Email had none, so an empty email silently disabled
                  Create account/Sign in with no on-screen explanation --
                  same gap fixed on profile/index.tsx's Handle field
                  (2026-09-03), audited across the rest of the app. */}
              {!email && (
                <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
                  Enter your email to continue.
                </Text>
              )}
            </YStack>
            <YStack gap="$1">
              <Label htmlFor="signup-password" color="$colorSecondary">
                Password
              </Label>
              <Input
                id="signup-password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                textContentType={mode === 'sign-up' ? 'newPassword' : 'password'}
              />
              {mode === 'sign-up' && (
                <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                  At least {MIN_PASSWORD_LENGTH} characters.
                </Text>
              )}
              {mode === 'sign-in' &&
                (forgotPassword.isSuccess ? (
                  /* Issue #151 AC3: this was plain $colorSecondary gray --
                     a real success outcome reading no differently from
                     any other helper text. Same feedbackSuccess* tokens
                     report-target/index.tsx and block-user/index.tsx
                     already use for their own success blocks, just at a
                     compact inline scale rather than a full-page one --
                     this sits inside a tight form, not as its own
                     screen state. */
                  <XStack
                    backgroundColor="$feedbackSuccessBg"
                    borderColor="$feedbackSuccessBorder"
                    borderWidth={borderWidth.hairline}
                    borderRadius="$sm"
                    px="$3"
                    py="$2"
                    alignSelf="flex-start"
                  >
                    <Text color="$feedbackSuccessText" fontSize="$meta" lineHeight="$meta">
                      Check your email for a reset link.
                    </Text>
                  </XStack>
                ) : (
                  <TextLink
                    alignSelf="flex-start"
                    fontSize="$meta"
                    lineHeight="$meta"
                    onPress={() => {
                      if (!forgotPassword.isPending) forgotPassword.mutate();
                    }}
                  >
                    {forgotPassword.isPending ? 'Sending...' : 'Forgot password?'}
                  </TextLink>
                ))}
            </YStack>
          </YStack>

          <PrimaryButton
            // MIN_PASSWORD_LENGTH only gates sign-UP -- it's a
            // constraint on what NEW passwords must satisfy, not a
            // property every existing credential has. Gating sign-in on
            // it too would lock out any real account whose password
            // predates this constant (e.g. one created directly via the
            // Supabase dashboard) even though Supabase Auth would
            // accept it -- a real regression an earlier version of this
            // fix introduced by gating both modes the same way (final
            // review, PR #51). Sign-in keeps its original, correct
            // gate: any non-empty password.
            disabled={!email || (mode === 'sign-up' ? password.length < MIN_PASSWORD_LENGTH : !password)}
            loading={emailAuth.isPending}
            onPress={() => {
              if (!emailAuth.isPending) emailAuth.mutate();
            }}
          >
            {emailAuth.isPending ? (
              <ActivityIndicator color="white" />
            ) : mode === 'sign-up' ? (
              'Create account'
            ) : (
              'Sign in'
            )}
          </PrimaryButton>
          <TextLink textAlign="center" onPress={() => setMode(mode === 'sign-up' ? 'sign-in' : 'sign-up')}>
            {mode === 'sign-up' ? 'Already have an account? Sign in' : 'New here? Sign up'}
          </TextLink>
        </YStack>

        {error && (
          <Paragraph color="$destructive" textAlign="center">
            {error.message}
          </Paragraph>
        )}
      </ScrollView>
    </ScreenContainer>
  );
}

function AgeGateStep({ onClose }: { onClose: () => void }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [attested, setAttested] = useState(false);

  const confirm = useMutation({
    mutationFn: () => api.signup(attested),
    onSuccess: (user) => queryClient.setQueryData(queryKeys.meSession(user.id), user),
  });

  return (
    <ScreenContainer>
      <YStack>
        <ScreenHeader label="Close" onBack={onClose} />
      </YStack>
      <YStack flex={1} alignItems="center" justifyContent="center" gap="$4">
        <Heading textAlign="center">One last thing</Heading>
        <Paragraph color="$colorSecondary" textAlign="center">
          Nearkin asks everyone to confirm they&apos;re 18 or older. Confirming your age is the first step — then
          you&apos;re in. Confirming you&apos;re a real person is optional and comes later, from your profile.
        </Paragraph>

        <XStack alignItems="center" gap="$3">
          {/* Checked indicator was $colorOnAction (#faf8f5) inside a
              frame whose own background defaults to $background
              (#faf8f5 in light mode -- the identical hex), measuring
              1.00:1 (invisible) in the whole-app critique. $primary
              both differs from every plausible frame background and is
              this app's own high-contrast action color. borderColor is
              explicit too: the frame's implicit default is
              $borderColor (1.27:1, decorative-only per this project's
              own contrast rules), not enough for an essential control
              boundary -- $borderColorStrong is. */}
          {/* Issue #151 AC6a: had no accessibilityRole/State/Label at
              all -- a screen reader gave no indication this row was a
              togglable checkbox, nor its current checked state. */}
          <Checkbox
            id="age-attestation"
            checked={attested}
            onCheckedChange={(value) => setAttested(value === true)}
            size="$body"
            borderColor="$borderColorStrong"
            accessibilityRole="checkbox"
            accessibilityState={{ checked: attested }}
            accessibilityLabel="I confirm I'm 18 or older"
          >
            <Checkbox.Indicator>
              <View width={12} height={12} backgroundColor="$primary" borderRadius="$full" />
            </Checkbox.Indicator>
          </Checkbox>
          <Pressable
            onPress={() => setAttested((value) => !value)}
            style={{ flexShrink: 1 }}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            <Label color="$color">I confirm I&apos;m 18 or older</Label>
          </Pressable>
        </XStack>

        {/* Issue #134: both documents now exist (terms/index.tsx,
            privacy/index.tsx) -- these were plain, non-interactive text
            until this fix, an agreement linking to nothing. Nested
            `Text` with its own `onPress`, not a full-line TextLink --
            these two links sit inline inside one sentence, not as
            their own standalone row. */}
        <Paragraph color="$colorTertiary" fontSize="$meta" lineHeight="$meta" textAlign="center">
          By continuing, you agree to Nearkin&apos;s{' '}
          {/* Underline + $color, not $colorLink -- issue #298: $colorLink
              is the exact same oxblood as PrimaryButton's fill, which made
              "Terms of Service" read at the same visual weight as "Save".
              A reference link inside a sentence isn't a primary action;
              the underline alone carries "this is tappable" without
              borrowing the app's one action color. */}
          <Text color="$color" textDecorationLine="underline" onPress={() => router.push('/terms')}>
            Terms of Service
          </Text>{' '}
          and{' '}
          <Text color="$color" textDecorationLine="underline" onPress={() => router.push('/privacy')}>
            Privacy Policy
          </Text>
          .
        </Paragraph>

        <PrimaryButton
          disabled={!attested}
          loading={confirm.isPending}
          onPress={() => {
            if (!confirm.isPending) confirm.mutate();
          }}
        >
          {confirm.isPending ? <ActivityIndicator color="white" /> : 'Continue'}
        </PrimaryButton>

        {confirm.isError && (
          <Paragraph color="$destructive" textAlign="center">
            {confirm.error.message || 'Something went wrong. Check your connection and try again.'}
          </Paragraph>
        )}
      </YStack>
    </ScreenContainer>
  );
}
